import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartModule } from 'primeng/chart';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { DailyChartData, DashboardChartDataService } from '../../services/dashboard-chart-data.service';

@Component({
  selector: 'app-parking-utilization-chart',
  imports: [CommonModule, TranslateModule, ChartModule],
  templateUrl: './parking-utilization-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParkingUtilizationChartComponent implements OnInit {
  private readonly chartDataService = inject(DashboardChartDataService);
  private readonly translateService = inject(TranslateService);

  protected readonly isLoading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly chartData = signal<any>(null);
  protected readonly chartOptions = signal<any>(null);

  ngOnInit() {
    this.loadChartData();
    this.initChartOptions();
  }

  private loadChartData() {
    this.isLoading.set(true);
    this.error.set(null);

    this.chartDataService.getParkingUtilizationData().subscribe({
      next: (dailyData) => {
        this.setupChartData(dailyData);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading parking utilization chart data:', error);
        this.error.set(this.translateService.instant('dashboard.charts.parkingUtilization.error'));
        this.isLoading.set(false);
      }
    });
  }

  private setupChartData(dailyData: DailyChartData[]) {
    const labels = dailyData.map((d) => {
      const date = new Date(d.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const percentages = dailyData.map((d) => d.percentage);

    // Calculate linear regression for trend line
    const trendData = this.calculateLinearRegression(percentages);

    this.chartData.set({
      labels: labels,
      datasets: [
        {
          label: this.translateService.instant('dashboard.charts.parkingUtilization.datasetLabel'),
          data: percentages,
          borderColor: '#7c3aed', // violet-600
          backgroundColor: 'rgba(124, 58, 237, 0.1)', // violet-600 with opacity
          tension: 0.4,
          fill: true
        },
        {
          label: 'Trend',
          data: trendData,
          borderColor: '#dc2626', // red-600
          borderDash: [5, 5],
          borderWidth: 2,
          backgroundColor: 'transparent',
          pointRadius: 0,
          tension: 0
        }
      ]
    });
  }

  private calculateLinearRegression(data: number[]): number[] {
    const n = data.length;
    let sumX = 0,
      sumY = 0,
      sumXY = 0,
      sumXX = 0;

    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += data[i];
      sumXY += i * data[i];
      sumXX += i * i;
    }

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return data.map((_, i) => slope * i + intercept);
  }

  private initChartOptions() {
    this.chartOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1.5,
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function (value: any) {
              return value + '%';
            }
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function (context: any) {
              if (context.datasetIndex === 0) {
                return `${context.dataset.label}: ${context.parsed.y}%`;
              } else {
                return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`;
              }
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    });
  }
}
